// @flow

// Libs
import _find from 'lodash/find'
import _round from 'lodash/round'
import { createSelector } from 'reselect'
import moment from 'moment'

import {
  // utils
  convertCommentsToFrontendUnits,
  // messages
  messages,
} from 'hfty-comments'

// DataTable Composer
import composeDataTable from 'fc-component-datatable'

// Hocs
import CommentsDataTableHOC from './hocs/CommentsDataTable'

// Utils
import { getCropUnit } from 'hfty-utils'

// Constants
import {
  PROP_ASSET_ID,
  PROP_LABEL,
} from 'fc-model-assets'
import {
  PROP_ID,
  PROP_FIELD,
  PROP_CROP,
  PROP_YIELD_TARGET,
  PROP_DATE_CREATED,
} from 'hfty-model-comments'
import {
  PROP_CROP_ID,
  PROP_NAME as PROP_CROP_NAME,
} from 'fc-model-crops'
import {
  PROP_SOIL_JOB_ID,
  PROP_FIELD as PROP_JOB_FIELD,
} from 'hfty-model-soil-jobs'
import {
  DATATABLE_NAME,
  ATTR_COMMENT_ID,
  ATTR_CROP,
  ATTR_YIELD_TARGET,
  ATTR_FIELD,
  ATTR_DATE_CREATED,
  ATTR_JOB_ID,
  ATTR_HAS_RECOMMENDED_RATES,
} from 'hfty-datatable-comments/src/constants'

// Types
import type { Comment } from 'hfty-model-comments/types'
import type { CommentRecord } from '../types'
import type {
  DataTableState,
  DataTableModule,
} from 'fc-component-datatable/types'
import type { Asset } from 'fc-model-assets/types'
import type { Crop } from 'fc-model-crops/types'
import type { SoilJob } from 'hfty-model-soil-jobs/types'
type ComposeCommentsDataTableArgs = {
  comments$: (state: Object) => Array<Comment>,
  fields$: (state: Object) => Array<Asset>,
  crops$: (state: Object) => Array<Crop>,
  jobs$: (state: Object) => Array<SoilJob>,
  tableState$: (state: Object) => DataTableState,
}

// Exports
export default function composeCommentsDataTable ({
  comments$,
  fields$,
  crops$,
  jobs$,
  tableState$,
  currentUserUnits$,
  }: ComposeCommentsDataTableArgs): DataTableModule<Comment, CommentRecord> {
  //
  function makeRecords (records$: (state: Object) => Array<Comment>): (state: Object) => Array<CommentRecord> {
    return createSelector(
      [records$, fields$, crops$, jobs$, currentUserUnits$],
      function (
        comments: Array<Comment>,
        fields: Array<Asset>,
        crops: Array<Crop>,
        jobs: Array<SoilJob>,
        currentUserUnits: string,
      ): Array<CommentRecord> {
        console.warn('Recompute Records')
        return comments.map((comment: Comment): CommentRecord => {
          const field: ?Asset = fields.find((field: Asset): boolean => field[PROP_ASSET_ID] === comment[PROP_FIELD])
          const job: ?SoilJob = jobs.find((job: SoilJob): boolean => job[PROP_JOB_FIELD] === comment[PROP_FIELD])
          const crop: ?Crop = _find(crops, {[PROP_CROP_ID]: comment[PROP_CROP]})
          const cropUnit = getCropUnit(crop, currentUserUnits)
          const convertedComment = convertCommentsToFrontendUnits(comment, crops, currentUserUnits)
          const targetYieldValue = _round(convertedComment[PROP_YIELD_TARGET], 2) || convertedComment[PROP_YIELD_TARGET]
          const targetYieldUnit = messages[cropUnit].defaultMessage
          const yieldTarget = `${targetYieldValue} ${targetYieldUnit}`

          return {
            [ATTR_COMMENT_ID]: convertedComment[PROP_ID],
            [ATTR_CROP]: crop ? crop[PROP_CROP_NAME] : '',
            [ATTR_YIELD_TARGET]: yieldTarget,
            [ATTR_FIELD]: (field && field[PROP_LABEL]) || '',
            [ATTR_DATE_CREATED]: moment.utc(convertedComment[PROP_DATE_CREATED]).local().format('YYYY-MM-DD hh:mm'),
            [ATTR_JOB_ID]: (job && job[PROP_SOIL_JOB_ID]) || null,
            [ATTR_HAS_RECOMMENDED_RATES]: true,
          }
        })
      }
    )
  }

  const datatable: DataTableModule<Comment, CommentRecord> = composeDataTable({name: DATATABLE_NAME})({
    data$: comments$,
    tableState$,
    makeRecords,
  })

  return {
    ...datatable,
    DataTable: CommentsDataTableHOC(datatable.DataTable),
  }
}
